import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';

export const FileUpload = ({ onUploadSuccess }) => {
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [uploadedExpenses, setUploadedExpenses] = useState([]);
  const [editingExpense, setEditingExpense] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [selectedExpenses, setSelectedExpenses] = useState([]); // For bulk delete
  const expensesRef = useRef([]);

  // Load recent expenses on component mount (for refresh persistence)
  useEffect(() => {
    const loadRecentExpenses = async () => {
      try {
        // First try to load from localStorage
        const savedExpenses = localStorage.getItem('uploadedExpenses');
        if (savedExpenses) {
          try {
            const parsed = JSON.parse(savedExpenses);
            if (Array.isArray(parsed) && parsed.length > 0) {
              console.log('Loaded expenses from localStorage:', parsed.length, 'items');
              expensesRef.current = parsed;
              setUploadedExpenses(parsed);
              return;
            }
          } catch (e) {
            console.warn('Failed to parse saved expenses from localStorage:', e);
          }
        }

        // If no localStorage data, fetch from API
        const response = await api.get('/upload/expenses/recent', { params: { limit: 100 } });
        if (response.data.expenses && response.data.expenses.length > 0) {
          console.log('Loaded recent expenses from API:', response.data.expenses.length, 'items');
          expensesRef.current = response.data.expenses;
          setUploadedExpenses(response.data.expenses);
          // Save to localStorage
          localStorage.setItem('uploadedExpenses', JSON.stringify(response.data.expenses));
        }
      } catch (error) {
        console.error('Failed to load recent expenses:', error);
      }
    };

    // Only load if we don't have expenses already
    if (!uploadedExpenses || uploadedExpenses.length === 0) {
      loadRecentExpenses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Save to localStorage and ref when uploadedExpenses changes
  useEffect(() => {
    console.log('uploadedExpenses state changed:', uploadedExpenses?.length, 'items');
    if (uploadedExpenses && uploadedExpenses.length > 0) {
      console.log('First expense:', uploadedExpenses[0]);
      expensesRef.current = uploadedExpenses; // Save to ref to prevent loss on re-render
      // Save to localStorage for persistence across refreshes
      localStorage.setItem('uploadedExpenses', JSON.stringify(uploadedExpenses));
    } else if ((!uploadedExpenses || uploadedExpenses.length === 0) && expensesRef.current.length > 0) {
      // If state was cleared but ref still has data, restore it after a short delay
      console.log('Restoring expenses from ref - state was cleared but ref has', expensesRef.current.length, 'items');
      setTimeout(() => {
        if (expensesRef.current.length > 0) {
          console.log('Actually restoring expenses now');
          setUploadedExpenses([...expensesRef.current]); // Create new array to trigger re-render
        }
      }, 50);
    } else if (!uploadedExpenses || uploadedExpenses.length === 0) {
      // Clear localStorage if expenses are cleared
      localStorage.removeItem('uploadedExpenses');
    }
  }, [uploadedExpenses]);

  const handleFileUpload = async (file, endpoint, type) => {
    setUploading(true);
    setMessage('');
    setUploadedExpenses([]);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post(`/upload/${endpoint}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setMessage(`✅ ${response.data.message || 'Fayl muvaffaqiyatli yuklandi'}`);
      
      // If expenses were uploaded, show them for editing
      if (endpoint === 'expenses') {
        if (response.data.expenses && Array.isArray(response.data.expenses) && response.data.expenses.length > 0) {
          console.log('Received expenses for editing:', response.data.expenses.length, 'items');
          console.log('Expenses data:', response.data.expenses);
          expensesRef.current = response.data.expenses; // Save to ref first
          setUploadedExpenses(response.data.expenses);
          console.log('Set uploadedExpenses state');
          
          // Don't call onUploadSuccess immediately for expenses - let user edit first
          // Call it only after user closes the table or after a longer delay
          // This prevents the component from re-rendering and losing state
        } else {
          console.warn('No expenses in response or empty array:', response.data);
          // Try to fetch recent expenses as fallback
          try {
            const recentResponse = await api.get('/upload/expenses/recent', { params: { limit: 50 } });
            if (recentResponse.data.expenses && recentResponse.data.expenses.length > 0) {
              console.log('Fetched recent expenses as fallback:', recentResponse.data.expenses.length);
              const expenses = recentResponse.data.expenses.slice(0, 50);
              expensesRef.current = expenses; // Save to ref first
              setUploadedExpenses(expenses);
              
              // Don't call onUploadSuccess immediately - let user edit first
            } else {
              // No expenses found, call onUploadSuccess immediately
              if (onUploadSuccess) {
                onUploadSuccess();
              }
            }
          } catch (err) {
            console.error('Failed to fetch recent expenses:', err);
            if (onUploadSuccess) {
              onUploadSuccess();
            }
          }
        }
      } else {
        // For income uploads, call onUploadSuccess immediately
        if (onUploadSuccess) {
          onUploadSuccess();
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          'Fayl yuklashda xatolik';
      setMessage(`❌ ${errorMessage}`);
    } finally {
      setUploading(false);
    }
  };

  const handleEditExpense = (expense) => {
    setEditingExpense(expense.id);
    setEditForm({
      year: expense.year,
      month: expense.month,
      day: expense.day,
      amount: expense.amount,
      expense_type: expense.expense_type,
      full_name: expense.full_name,
      region: expense.region,
      tulov_turi: expense.tulov_turi || '',
      comment: expense.comment || ''
    });
  };

  const handleSaveExpense = async (id) => {
    setSaving(true);
    try {
      const response = await api.put(`/upload/expenses/${id}`, editForm);
      setMessage(`✅ ${response.data.message || 'Xarajat yangilandi'}`);
      
      // Update the expense in the list and localStorage
      setUploadedExpenses(prev => {
        const updated = prev.map(exp => 
          exp.id === id ? { ...exp, ...editForm } : exp
        );
        expensesRef.current = updated;
        // Update localStorage
        localStorage.setItem('uploadedExpenses', JSON.stringify(updated));
        return updated;
      });
      
      setEditingExpense(null);
      setEditForm({});
      
      // Refresh stats
      if (onUploadSuccess) {
        setTimeout(() => {
          onUploadSuccess();
        }, 100);
      }
    } catch (error) {
      console.error('Update error:', error);
      const errorMessage = error.response?.data?.error || 'Xarajatni yangilashda xatolik';
      setMessage(`❌ ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm('Haqiqatan ham bu xarajatni o\'chirmoqchimisiz?')) {
      return;
    }

    setDeleting(id);
    try {
      await api.delete(`/upload/expenses/${id}`);
      setMessage('✅ Xarajat o\'chirildi');
      
      // Remove from list and update localStorage
      setUploadedExpenses(prev => {
        const updated = prev.filter(exp => exp.id !== id);
        expensesRef.current = updated;
        // Update localStorage
        if (updated.length > 0) {
          localStorage.setItem('uploadedExpenses', JSON.stringify(updated));
        } else {
          localStorage.removeItem('uploadedExpenses');
        }
        return updated;
      });
      
      // Remove from selected if it was selected
      setSelectedExpenses(prev => prev.filter(expId => expId !== id));
      
      // Refresh stats
      if (onUploadSuccess) {
        setTimeout(() => {
          onUploadSuccess();
        }, 100);
      }
    } catch (error) {
      console.error('Delete error:', error);
      const errorMessage = error.response?.data?.error || 'Xarajatni o\'chirishda xatolik';
      setMessage(`❌ ${errorMessage}`);
    } finally {
      setDeleting(null);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedExpenses.length === 0) {
      setMessage('❌ Hech qanday xarajat tanlanmagan');
      return;
    }

    if (!window.confirm(`Haqiqatan ham ${selectedExpenses.length} ta xarajatni o'chirmoqchimisiz?`)) {
      return;
    }

    setDeleting('bulk');
    try {
      // Delete all selected expenses with error handling for each
      const deleteResults = await Promise.allSettled(
        selectedExpenses.map(id => api.delete(`/upload/expenses/${id}`))
      );
      
      // Count successful and failed deletions
      const successful = deleteResults.filter(result => result.status === 'fulfilled').length;
      const failed = deleteResults.filter(result => result.status === 'rejected').length;
      
      // Remove successfully deleted expenses from list
      const failedIds = deleteResults
        .map((result, index) => result.status === 'rejected' ? selectedExpenses[index] : null)
        .filter(id => id !== null);
      
      const successfullyDeletedIds = selectedExpenses.filter(id => !failedIds.includes(id));
      
      // Update the list
      setUploadedExpenses(prev => {
        const updated = prev.filter(exp => !successfullyDeletedIds.includes(exp.id));
        expensesRef.current = updated;
        // Update localStorage
        if (updated.length > 0) {
          localStorage.setItem('uploadedExpenses', JSON.stringify(updated));
        } else {
          localStorage.removeItem('uploadedExpenses');
        }
        return updated;
      });
      
      // Clear selection
      setSelectedExpenses([]);
      
      // Show appropriate message
      if (successful > 0 && failed === 0) {
        setMessage(`✅ ${successful} ta xarajat muvaffaqiyatli o'chirildi`);
      } else if (successful > 0 && failed > 0) {
        setMessage(`⚠️ ${successful} ta xarajat o'chirildi, ${failed} ta xarajat topilmadi (allaqachon o'chirilgan bo'lishi mumkin)`);
      } else {
        setMessage(`❌ Hech qanday xarajat o'chirilmadi. Barcha xarajatlar topilmadi yoki allaqachon o'chirilgan.`);
      }
      
      // Always call onUploadSuccess to refresh stats
      if (onUploadSuccess && successful > 0) {
        setTimeout(() => {
          onUploadSuccess();
        }, 100);
      }
    } catch (error) {
      console.error('Bulk delete error:', error);
      const errorMessage = error.response?.data?.error || 'Xarajatlarni o\'chirishda xatolik';
      setMessage(`❌ ${errorMessage}`);
    } finally {
      setDeleting(null);
    }
  };

  const handleSelectExpense = (id) => {
    setSelectedExpenses(prev => 
      prev.includes(id) 
        ? prev.filter(expId => expId !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedExpenses.length === uploadedExpenses.length) {
      setSelectedExpenses([]);
    } else {
      setSelectedExpenses(uploadedExpenses.map(exp => exp.id));
    }
  };

  const handleCancelEdit = () => {
    setEditingExpense(null);
    setEditForm({});
  };

  return (
    <>
      <div style={{ padding: '1.5rem', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <h3>Fayllarni Yuklash</h3>
      
      <div style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
          Xarajatlar Excel Fayli
        </label>
        <input
          type="file"
          accept=".xlsx,.xls"
          onChange={(e) => {
            if (e.target.files[0]) {
              handleFileUpload(e.target.files[0], 'expenses', 'xarajat');
            }
          }}
          disabled={uploading}
          style={{
            width: '100%',
            padding: '0.5rem',
            border: '1px solid #ddd',
            borderRadius: '4px'
          }}
        />
        <small style={{ color: '#666' }}>
          Ustunlar: yil, oy, kun, summa, xarajat_turi, ism_familiya, region
        </small>
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
          Savdo Excel Fayli
        </label>
        <input
          type="file"
          accept=".xlsx,.xls"
          onChange={(e) => {
            if (e.target.files[0]) {
              handleFileUpload(e.target.files[0], 'income', 'savdo');
            }
          }}
          disabled={uploading}
          style={{
            width: '100%',
            padding: '0.5rem',
            border: '1px solid #ddd',
            borderRadius: '4px'
          }}
        />
        <small style={{ color: '#666' }}>
          Ustunlar: yil, oy, summa, ism_familiya, region
        </small>
      </div>

      {uploading && (
        <div style={{ 
          color: '#007bff', 
          textAlign: 'center', 
          marginTop: '1rem',
          fontSize: '0.9rem'
        }}>
          Fayl yuklanmoqda...
        </div>
      )}

      {message && (
        <div style={{ 
          marginTop: '1rem',
          padding: '0.75rem',
          backgroundColor: message.includes('✅') ? '#d4edda' : '#f8d7da',
          color: message.includes('✅') ? '#155724' : '#721c24',
          borderRadius: '4px',
          fontSize: '0.9rem'
        }}>
          {message}
        </div>
      )}
      </div>

      {/* Editable Expenses Table - Static, always shown if there are expenses */}
      {(uploadedExpenses && uploadedExpenses.length > 0) && (
        <div 
          id="expenses-edit-table"
          style={{ 
            marginTop: '2rem',
            padding: '1.5rem', 
            backgroundColor: '#ffffff', 
            borderRadius: '8px', 
            border: '2px solid #007bff',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            width: '100%',
            position: 'relative',
            zIndex: 1,
            display: 'block'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <h4 style={{ margin: 0, color: '#212529', fontSize: '1.25rem', fontWeight: '600' }}>
                Yuklangan Xarajatlar (Tahrirlash mumkin)
              </h4>
              <span style={{ 
                padding: '0.25rem 0.75rem', 
                backgroundColor: '#007bff', 
                color: 'white', 
                borderRadius: '20px',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}>
                {uploadedExpenses.length} ta
              </span>
              {selectedExpenses.length > 0 && (
                <span style={{ 
                  padding: '0.25rem 0.75rem', 
                  backgroundColor: '#dc3545', 
                  color: 'white', 
                  borderRadius: '20px',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}>
                  {selectedExpenses.length} ta tanlangan
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {selectedExpenses.length > 0 && (
                <button
                  onClick={handleBulkDelete}
                  disabled={deleting === 'bulk'}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: deleting === 'bulk' ? 'not-allowed' : 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}
                >
                  {deleting === 'bulk' ? 'O\'chirilmoqda...' : `Tanlanganlarni o'chirish (${selectedExpenses.length})`}
                </button>
              )}
              <button
                onClick={() => {
                  if (window.confirm('Haqiqatan ham jadvalni tozalashni xohlaysizmi? Bu barcha yuklangan xarajatlarni jadvaldan olib tashlaydi.')) {
                    setUploadedExpenses([]);
                    expensesRef.current = [];
                    setSelectedExpenses([]);
                    localStorage.removeItem('uploadedExpenses'); // Clear localStorage
                    setMessage('✅ Jadval tozalandi');
                    // Call onUploadSuccess when user clears the table
                    if (onUploadSuccess) {
                      setTimeout(() => {
                        onUploadSuccess();
                      }, 100);
                    }
                  }
                }}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}
              >
                Jadvalni tozalash
              </button>
            </div>
          </div>
          <div style={{ 
            maxHeight: '600px', 
            overflowY: 'auto', 
            border: '1px solid #dee2e6', 
            borderRadius: '4px',
            backgroundColor: '#fff'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', minWidth: '1200px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 1 }}>
                  <th style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '1px solid #dee2e6', borderRight: '1px solid #dee2e6', width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={selectedExpenses.length === uploadedExpenses.length && uploadedExpenses.length > 0}
                      onChange={handleSelectAll}
                      style={{ cursor: 'pointer' }}
                    />
                  </th>
                  <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #dee2e6', borderRight: '1px solid #dee2e6' }}>Sana</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #dee2e6', borderRight: '1px solid #dee2e6' }}>Summa</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #dee2e6', borderRight: '1px solid #dee2e6' }}>Xarajat turi</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #dee2e6', borderRight: '1px solid #dee2e6' }}>Foydalanuvchi</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #dee2e6', borderRight: '1px solid #dee2e6' }}>Region</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #dee2e6', borderRight: '1px solid #dee2e6' }}>To'lov turi</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #dee2e6', borderRight: '1px solid #dee2e6' }}>Izoh</th>
                  <th style={{ padding: '0.5rem', textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>Amallar</th>
                </tr>
              </thead>
              <tbody>
                {uploadedExpenses.map((expense) => (
                  <tr key={expense.id} style={{ backgroundColor: selectedExpenses.includes(expense.id) ? '#fff3cd' : 'transparent' }}>
                    <td style={{ padding: '0.5rem', borderBottom: '1px solid #dee2e6', borderRight: '1px solid #dee2e6', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selectedExpenses.includes(expense.id)}
                        onChange={() => handleSelectExpense(expense.id)}
                        disabled={editingExpense === expense.id}
                        style={{ cursor: 'pointer' }}
                      />
                    </td>
                    {editingExpense === expense.id ? (
                      <>
                        <td style={{ padding: '0.5rem', borderBottom: '1px solid #dee2e6', borderRight: '1px solid #dee2e6' }}>
                          <div style={{ display: 'flex', gap: '0.25rem' }}>
                            <input
                              type="number"
                              value={editForm.day || ''}
                              onChange={(e) => setEditForm({...editForm, day: parseInt(e.target.value)})}
                              placeholder="Kun"
                              style={{ width: '50px', padding: '0.25rem', border: '1px solid #ddd', borderRadius: '4px' }}
                            />
                            <input
                              type="number"
                              value={editForm.month || ''}
                              onChange={(e) => setEditForm({...editForm, month: parseInt(e.target.value)})}
                              placeholder="Oy"
                              style={{ width: '50px', padding: '0.25rem', border: '1px solid #ddd', borderRadius: '4px' }}
                            />
                            <input
                              type="number"
                              value={editForm.year || ''}
                              onChange={(e) => setEditForm({...editForm, year: parseInt(e.target.value)})}
                              placeholder="Yil"
                              style={{ width: '70px', padding: '0.25rem', border: '1px solid #ddd', borderRadius: '4px' }}
                            />
                          </div>
                        </td>
                        <td style={{ padding: '0.5rem', borderBottom: '1px solid #dee2e6', borderRight: '1px solid #dee2e6' }}>
                          <input
                            type="number"
                            step="0.01"
                            value={editForm.amount || ''}
                            onChange={(e) => setEditForm({...editForm, amount: parseFloat(e.target.value)})}
                            style={{ width: '100px', padding: '0.25rem', border: '1px solid #ddd', borderRadius: '4px' }}
                          />
                        </td>
                        <td style={{ padding: '0.5rem', borderBottom: '1px solid #dee2e6', borderRight: '1px solid #dee2e6' }}>
                          <input
                            type="text"
                            value={editForm.expense_type || ''}
                            onChange={(e) => setEditForm({...editForm, expense_type: e.target.value})}
                            style={{ width: '120px', padding: '0.25rem', border: '1px solid #ddd', borderRadius: '4px' }}
                          />
                        </td>
                        <td style={{ padding: '0.5rem', borderBottom: '1px solid #dee2e6', borderRight: '1px solid #dee2e6' }}>
                          <input
                            type="text"
                            value={editForm.full_name || ''}
                            onChange={(e) => setEditForm({...editForm, full_name: e.target.value})}
                            style={{ width: '150px', padding: '0.25rem', border: '1px solid #ddd', borderRadius: '4px' }}
                          />
                        </td>
                        <td style={{ padding: '0.5rem', borderBottom: '1px solid #dee2e6', borderRight: '1px solid #dee2e6' }}>
                          <input
                            type="text"
                            value={editForm.region || ''}
                            onChange={(e) => setEditForm({...editForm, region: e.target.value})}
                            style={{ width: '120px', padding: '0.25rem', border: '1px solid #ddd', borderRadius: '4px' }}
                          />
                        </td>
                        <td style={{ padding: '0.5rem', borderBottom: '1px solid #dee2e6', borderRight: '1px solid #dee2e6' }}>
                          <input
                            type="text"
                            value={editForm.tulov_turi || ''}
                            onChange={(e) => setEditForm({...editForm, tulov_turi: e.target.value})}
                            style={{ width: '100px', padding: '0.25rem', border: '1px solid #ddd', borderRadius: '4px' }}
                          />
                        </td>
                        <td style={{ padding: '0.5rem', borderBottom: '1px solid #dee2e6', borderRight: '1px solid #dee2e6' }}>
                          <input
                            type="text"
                            value={editForm.comment || ''}
                            onChange={(e) => setEditForm({...editForm, comment: e.target.value})}
                            style={{ width: '150px', padding: '0.25rem', border: '1px solid #ddd', borderRadius: '4px' }}
                          />
                        </td>
                        <td style={{ padding: '0.5rem', borderBottom: '1px solid #dee2e6', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
                            <button
                              onClick={() => handleSaveExpense(expense.id)}
                              disabled={saving}
                              style={{
                                padding: '0.25rem 0.5rem',
                                backgroundColor: '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: saving ? 'not-allowed' : 'pointer',
                                fontSize: '0.75rem'
                              }}
                            >
                              {saving ? 'Saqlanmoqda...' : 'Saqlash'}
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              disabled={saving}
                              style={{
                                padding: '0.25rem 0.5rem',
                                backgroundColor: '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: saving ? 'not-allowed' : 'pointer',
                                fontSize: '0.75rem'
                              }}
                            >
                              Bekor
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={{ padding: '0.5rem', borderBottom: '1px solid #dee2e6', borderRight: '1px solid #dee2e6' }}>
                          {`${expense.day || ''}/${expense.month || ''}/${expense.year || ''}`}
                        </td>
                        <td style={{ padding: '0.5rem', borderBottom: '1px solid #dee2e6', borderRight: '1px solid #dee2e6' }}>
                          {parseFloat(expense.amount || 0).toLocaleString()}
                        </td>
                        <td style={{ padding: '0.5rem', borderBottom: '1px solid #dee2e6', borderRight: '1px solid #dee2e6' }}>
                          {expense.expense_type || ''}
                        </td>
                        <td style={{ padding: '0.5rem', borderBottom: '1px solid #dee2e6', borderRight: '1px solid #dee2e6' }}>
                          {expense.full_name || ''}
                        </td>
                        <td style={{ padding: '0.5rem', borderBottom: '1px solid #dee2e6', borderRight: '1px solid #dee2e6' }}>
                          {expense.region || ''}
                        </td>
                        <td style={{ padding: '0.5rem', borderBottom: '1px solid #dee2e6', borderRight: '1px solid #dee2e6' }}>
                          {expense.tulov_turi || ''}
                        </td>
                        <td style={{ padding: '0.5rem', borderBottom: '1px solid #dee2e6', borderRight: '1px solid #dee2e6' }}>
                          {expense.comment || ''}
                        </td>
                        <td style={{ padding: '0.5rem', borderBottom: '1px solid #dee2e6', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
                            <button
                              onClick={() => handleEditExpense(expense)}
                              style={{
                                padding: '0.25rem 0.5rem',
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.75rem'
                              }}
                            >
                              Tahrirlash
                            </button>
                            <button
                              onClick={() => handleDeleteExpense(expense.id)}
                              disabled={deleting === expense.id}
                              style={{
                                padding: '0.25rem 0.5rem',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: deleting === expense.id ? 'not-allowed' : 'pointer',
                                fontSize: '0.75rem'
                              }}
                            >
                              {deleting === expense.id ? 'O\'chirilmoqda...' : 'O\'chirish'}
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};
